import { useSearch } from "@tanstack/react-router";
import { AlertCircle, ExternalLink, Info } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { Ping } from "@/components/ui/ping";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { TranslationKey } from "@/localisation/types";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  TypographyMuted,
  TypographyP,
  TypographySmall,
} from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { useGetAllDatevTokens } from "@/queries/datev-tokens/get-all";

import { Checkbox } from "@/components/ui/checkbox";
import { DatevSignInButton } from "./sign-in-button";
import { SignOutButton } from "./sign-out-button";

type ConnectionStatusProps = {
  connected: boolean;
  error?: string;
};

const ConnectionStatus = ({ connected, error }: ConnectionStatusProps) => {
  const { t } = useTranslation();

  return (
    <Badge variant="outline" className="gap-1">
      {error ? (
        <>
          <Ping variant="warning" />
          <span>
            {
              t(
                `integrations.datev.warnings.${error}` as TranslationKey,
              ) as string
            }
          </span>
        </>
      ) : (
        <>
          <Ping variant={connected ? "success" : "destructive"} />
          <span>
            {t(
              `integrations.datev.${
                connected ? "connected" : "disconnected"
              }_status`,
            )}
          </span>
        </>
      )}
    </Badge>
  );
};

const SubStepBullet = ({
  letter,
  children,
}: { letter: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-2 text-sm text-muted-foreground">
    <span className="flex-shrink-0 w-5 h-5 bg-muted text-muted-foreground rounded-full flex items-center justify-center text-xs font-normal">
      {letter}
    </span>
    <span>{children}</span>
  </div>
);

const ExternalLinkButton = ({
  href,
  children,
}: { href: string; children: React.ReactNode }) => (
  <Button
    onClick={(e) => {
      e.stopPropagation();
      window.open(href, "_blank");
    }}
    className="px-2 py-1 text-xs border border-gray-300 bg-gray-100 font-medium flex items-center h-6 text-gray-500 hover:text-gray-600 hover:bg-gray-200 hover:border-gray-300"
  >
    <ExternalLink width={12} className="mr-1" />
    {children}
  </Button>
);

export const DatevSetup: React.FC = () => {
  const { t } = useTranslation();
  const [clientId, setClientId] = useState<string>("");
  const [step1Check, setStep1Check] = useState<boolean>(false);
  const search = useSearch({ strict: false });

  const errors = (search as { error?: string }).error;

  const [taxAdvisorNumber, clientNumber] = useMemo(() => {
    if (!clientId) {
      return ["", ""];
    }

    return clientId.split("-");
  }, [clientId]);

  const { tokens, loading, refetch } = useGetAllDatevTokens({
    fetchPolicy: "network-only",
    skipPermissionsCheck: false,
  });

  const token = tokens?.[0];

  useEffect(() => {
    if (token) {
      setClientId(token.clientId);
    }
  }, [token]);

  useEffect(() => {
    if (errors) {
      setTimeout(() => {
        toast.error(t(`server_errors.${errors}` as TranslationKey) as string, {
          richColors: true,
        });
      }, 500);
    }
  }, [errors, t]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // FIXME: this is a quick solution we need to split the values in the backend
  const handleSetTaxAdvisorNumber = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (!clientNumber) {
      setClientId(`${value}-`);
      return;
    }
    setClientId(`${value}-${clientNumber}`);
  };

  // FIXME: this is a quick solution we need to split the values in the backend
  const handleSetClientNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!taxAdvisorNumber) {
      setClientId(`-${value}`);
      return;
    }
    setClientId(`${taxAdvisorNumber}-${value}`);
  };

  const isValueValid = taxAdvisorNumber && clientNumber;

  const handleDisconnectSuccess = async () => {
    await refetch();
  };

  const connected = new Date(token?.refreshTokenExpirationDate) > new Date();

  const tokenIssues = useMemo(() => {
    return (
      new Date(token?.refreshTokenExpirationDate) < new Date() || token?.error
    );
  }, [token]);

  const isStep1CheckValid = useMemo(() => {
    return step1Check || token?.clientId;
  }, [step1Check, token?.clientId]);

  const isStep2Valid = useMemo(() => {
    return isStep1CheckValid && isValueValid;
  }, [isStep1CheckValid, isValueValid]);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          {tokenIssues && (
            <Alert variant="warning" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                {t("integrations.datev.token_issues.title")}
              </AlertTitle>
              <AlertDescription className="whitespace-pre-line">
                {t("integrations.datev.token_issues.description.title")}
              </AlertDescription>
              <ul className="list-decimal ml-2">
                <li>
                  {t("integrations.datev.token_issues.description.step_1")}
                </li>
                <li>
                  {t("integrations.datev.token_issues.description.step_2")}
                </li>
              </ul>
            </Alert>
          )}
          <CardTitle>{t("integrations.datev.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 items-center">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <TypographyP className="text-muted-foreground">1</TypographyP>
              </div>
              <div className="flex flex-col gap-4">
                <TypographySmall className="font-semibold">
                  {t("integrations.datev.step_1")}
                </TypographySmall>
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex justify-center h-auto w-10">
                <Separator orientation="vertical" />
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <div className="flex items-center gap-1.5 w-2/3">
                  <span className="text-sm text-muted-foreground">
                    {t("integrations.datev.step_1_details_label")}
                  </span>
                  <ExternalLinkButton href="https://tabula-accounting.notion.site/Export-Buchungsdatenservice-einrichten-10deecb29d0480eab835c0cc29f8738d">
                    {t("integrations.datev.step_1_button")}
                  </ExternalLinkButton>
                </div>
                <div className="mt-2 mb-4 w-2/3">
                  <div className="space-y-2">
                    <SubStepBullet letter="a">
                      {t("integrations.datev.step_1_instructions.1")}
                    </SubStepBullet>
                    <SubStepBullet letter="b">
                      {t("integrations.datev.step_1_instructions.2")}
                    </SubStepBullet>
                    <SubStepBullet letter="c">
                      {t("integrations.datev.step_1_instructions.3")}
                    </SubStepBullet>
                    <SubStepBullet letter="d">
                      {t("integrations.datev.step_1_instructions.4")}
                    </SubStepBullet>
                    <SubStepBullet letter="e">
                      {t("integrations.datev.step_1_instructions.5")}
                    </SubStepBullet>
                  </div>
                </div>
                <div className="flex flex-row gap-2 items-center mb-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      // biome-ignore lint/complexity/noUselessTernary: check state contains undetermined this solves this
                      checked={isStep1CheckValid ? true : false}
                      onCheckedChange={(checked) => {
                        setStep1Check(Boolean(checked));
                      }}
                      disabled={loading}
                    />
                    <TypographySmall className={cn(loading && "opacity-40")}>
                      {t("integrations.datev.step_1_check")}
                    </TypographySmall>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div
            className={cn(
              "flex flex-col gap-2",
              !isStep1CheckValid && "opacity-40",
            )}
          >
            <div className="flex flex-row gap-2 items-center">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <TypographyP className="text-muted-foreground">2</TypographyP>
              </div>
              <div className="flex flex-col gap-4">
                <TypographySmall className="font-semibold">
                  {t("integrations.datev.step_2")}
                </TypographySmall>
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex justify-center h-auto w-10">
                <Separator orientation="vertical" />
              </div>
              <div className="flex-1">
                <form>
                  <div className="grid w-full items-center gap-4 mb-2">
                    <div className="flex flex-col space-y-2">
                      <Label htmlFor="taxAdvisorNumber">
                        {t("integrations.datev.tax_advisor_number")}
                      </Label>
                      <p className="text-sm text-muted-foreground w-2/3">
                        {t("integrations.datev.step_2_description")}
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          id="taxAdvisorNumber"
                          placeholder={t(
                            "integrations.datev.tax_advisor_number_placeholder",
                          )}
                          value={taxAdvisorNumber}
                          disabled={!!token || loading || !isStep1CheckValid}
                          loading={loading}
                          onChange={handleSetTaxAdvisorNumber}
                          className="w-1/4"
                        />
                        <ExternalLinkButton href="https://tabula-accounting.notion.site/Export-Wo-finde-ich-die-Nummern-die-ich-in-Tabular-angeben-muss-um-den-Buchungsdatenservice-zu-v-25aeecb29d0480008898fa6c66af19fa">
                          {t("integrations.datev.step_2_numbers_button")}
                        </ExternalLinkButton>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <Label htmlFor="clientNumber">
                        {t("integrations.datev.client_number")}
                      </Label>
                      <Input
                        id="clientNumber"
                        placeholder={t(
                          "integrations.datev.client_number_placeholder",
                        )}
                        value={clientNumber}
                        disabled={!!token || loading || !isStep1CheckValid}
                        loading={loading}
                        onChange={handleSetClientNumber}
                        className="w-1/4"
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div
            className={cn("flex flex-col gap-2", !isStep2Valid && "opacity-40")}
          >
            <div className="flex flex-row gap-2 items-center">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <TypographyP className="text-muted-foreground">3</TypographyP>
              </div>
              <div className="flex flex-col gap-4">
                <TypographySmall className="font-semibold">
                  {t("integrations.datev.step_3")}
                </TypographySmall>
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex justify-center h-auto w-10" />
              <div className="w-full">
                {token ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row gap-8">
                      <div className="flex flex-col gap-1">
                        <TypographySmall>
                          {`${t("integrations.datev.enabled_by")}:`}
                        </TypographySmall>
                        <TypographyMuted>{token.issuedBy}</TypographyMuted>
                      </div>
                      <div className="flex flex-col gap-1">
                        <TypographySmall>
                          {`${t("integrations.datev.connected_on")}:`}
                        </TypographySmall>
                        <TypographyMuted>
                          {formatDate(new Date(token.createdAt))}
                        </TypographyMuted>
                      </div>
                      <div className="flex flex-col gap-1">
                        <TypographySmall>
                          {`${t("integrations.datev.connection_status")}:`}
                        </TypographySmall>
                        <div>
                          <ConnectionStatus
                            connected={connected}
                            error={token.error}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <TypographySmall>
                          {`${t("integrations.datev.expires_on")}:`}
                        </TypographySmall>
                        <TypographyMuted>
                          {formatDate(
                            new Date(token.refreshTokenExpirationDate),
                          )}
                        </TypographyMuted>
                      </div>
                    </div>

                    <div className="flex flex-row items-center justify-end gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="p-3 hover:bg-gray-100 rounded-full"
                            >
                              <Info size={16} className="text-gray-500" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            className="bg-white text-black border border-gray-200 shadow-md font-normal"
                            side="top"
                          >
                            <div className="max-w-sm">
                              <Trans
                                i18nKey={"integrations.datev.about_content"}
                                components={{
                                  Link: (
                                    <Link
                                      className="underline font-semibold"
                                      href={{
                                        pathname:
                                          "https://apps.datev.de/tokrevui",
                                      }}
                                      target="_blank"
                                    />
                                  ),
                                }}
                              />
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <SignOutButton
                        tokenId={token.datevTokenId}
                        onSuccess={handleDisconnectSuccess}
                      />
                    </div>
                  </div>
                ) : (
                  <DatevSignInButton
                    clientId={clientId}
                    isDisabled={!isStep2Valid}
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
